import { useState, useMemo, useEffect } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import { useVideo } from "../../../hooks/useVideo";
import LoadingSpinner from "../../loader/Loader";
import {
  useDeleteCategoryMutation,
  useGetCategoriesQuery,
  useReorderCategoriesMutation,
} from "../../../features/api/videoCategoriesApiSlice";
import ConfirmationModal from "../../confirmation/ConfirmationModal";
import { generatePageNumbers } from "../../../util/generatePageNumber";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const CategoryList = () => {
  const { formatDate } = useVideo();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteCategory] = useDeleteCategoryMutation();
  const [categoryFilter, setCategoryFilter] = useState("");
  const [reorderCategories] = useReorderCategoriesMutation();
  const [orderedCategories, setOrderedCategories] = useState([]);

  // ✅ Add pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 6; // categories per page

  const { data: categories, isLoading, isError } = useGetCategoriesQuery();

  // Filtered categories
  const filteredCategories = useMemo(() => {
    if (!categories) return [];
    return categories.filter(
      (tx) =>
        !categoryFilter ||
        `${tx?.name}`.toLowerCase().includes(categoryFilter.toLowerCase())
    );
  }, [categories, categoryFilter]);

  const totalPages = Math.ceil(filteredCategories.length / pageSize);

  const currentCategories = useMemo(() => {
    const startIdx = (currentPage - 1) * pageSize;
    return filteredCategories.slice(startIdx, startIdx + pageSize);
  }, [filteredCategories, currentPage, pageSize]);

  const pageNumbers = useMemo(
    () => generatePageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = (categoryId) => {
    setCategoryToDelete(categoryId);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    try {
      const response = await deleteCategory({
        categoryId: categoryToDelete,
      }).unwrap();
      toast.success(response?.message || "Category deleted successfully!");
      setCategoryToDelete(null);
      setIsModalOpen(false);
    } catch (err) {
      console.error("Failed to delete category:", err);
      toast.error(err?.data?.message || "Failed to category video.");
    }
  };

  const handleCancelDelete = () => {
    setCategoryToDelete(null);
    setIsModalOpen(false);
  };

  const SortableRow = ({ category, index, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: category._id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      cursor: "grab",
    };

    return (
      <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </tr>
    );
  };

  useEffect(() => {
    if (filteredCategories.length) {
      setOrderedCategories(filteredCategories);
    }
  }, [filteredCategories]);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = orderedCategories.findIndex((c) => c._id === active.id);
    const newIndex = orderedCategories.findIndex((c) => c._id === over.id);

    const updated = [...orderedCategories];
    const [moved] = updated.splice(oldIndex, 1);
    updated.splice(newIndex, 0, moved);

    setOrderedCategories(updated);

    try {
      await reorderCategories(updated.map((c) => c._id)).unwrap();
      toast.success("Category order updated");
    } catch (err) {
      toast.error("Failed to save category order");
    }
  };

  const isReordering = categoryFilter !== "";

  if (isLoading) return <LoadingSpinner />;

  if (!categories || categories?.length === 0) {
    return (
      <div className="no-content">
        <div>
          <p>No categories found</p>
        </div>
      </div>
    );
  } else if (isError)
    return (
      <div className="no-content">
        <div>
          <p>Error loading categories</p>
        </div>
      </div>
    );

  return (
    <div className="transactions-container">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Video Categories</h1>
          <button onClick={() => navigate("/admin/categories/add-category")}>
            Add Category
          </button>
        </div>
      </div>
      <div className="filters">
        <input
          type="text"
          placeholder="Filter by category name"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        />
      </div>

      <table className="transactions-table">
        <thead>
          <tr>
            <th>No.</th>
            <th>Name</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={orderedCategories.map((c) => c._id)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>
              {orderedCategories.map((tx, index) => (
                <SortableRow key={tx._id} category={tx}>
                  <td>{index + 1}</td>
                  <td>{tx.name}</td>
                  <td>{formatDate(tx.createdAt)}</td>
                  <td className="actions-container">
                    <button
                      className="refund-btn"
                      onClick={() => handleDeleteClick(tx._id)}
                    >
                      Delete
                    </button>
                    <Link
                      className="ed-button"
                      to={`/admin/categories/edit-category/${tx._id}`}
                    >
                      Edit
                    </Link>
                  </td>
                </SortableRow>
              ))}
            </tbody>
          </SortableContext>
        </DndContext>
        <ConfirmationModal
          isOpen={isModalOpen}
          message="Are you sure you want to delete this category? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={handleCancelDelete}
        />
      </table>
      {/* ✅ Pagination */}
      {totalPages > 1 && (
        <div className="pagination responsive-pagination">
          <button
            onClick={() => handlePageClick(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            {"<"}
          </button>
          {pageNumbers.map((page, idx) =>
            page === "..." ? (
              <span className="ellipsis" key={idx}>
                ...
              </span>
            ) : (
              <button
                key={idx}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageClick(page)}
              >
                {page}
              </button>
            )
          )}
          <button
            onClick={() =>
              handlePageClick(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            {">"}
          </button>
        </div>
      )}
    </div>
  );
};

export default CategoryList;
